import * as React from 'react'

import { getElementSizeInEm, targetWidths } from '../utils/getElementSize'
import type { EmUnit, NumberSegment } from '../types'

type MeasurementAction = {
  element: HTMLElement
  type: 'remove' | 'width'
  nextSibling: Node | null
  oldWidth?: string
  newWidth?: string
}

/**
 * Custom hook to handle section measurements and animations
 */
export const useMeasureSection = (
  measuredRef: React.RefObject<HTMLSpanElement>,
  isFirstRender: boolean,
  segments: NumberSegment[],
): EmUnit | undefined => {
  const [width, setWidth] = React.useState<EmUnit>()

  React.useEffect(() => {
    const element = measuredRef.current
    if (!element) return

    if (isFirstRender) {
      setWidth(getElementSizeInEm(element).width)
      return
    }

    const actions: MeasurementAction[] = []
    const children = Array.from(element.children)

    // Process each child element
    children.forEach((child) => {
      if (!(child instanceof HTMLElement)) return

      if (child.dataset.state === 'exiting') {
        actions.push({
          element: child,
          type: 'remove',
          nextSibling: child.nextSibling,
        })
        child.remove()
      } else {
        const newWidth = targetWidths.get(child)
        if (newWidth) {
          actions.push({
            element: child,
            type: 'width',
            nextSibling: null,
            oldWidth: child.style.width,
            newWidth,
          })
          child.style.width = newWidth
        }
      }
    })

    // Update width measurement
    setWidth(getElementSizeInEm(element).width)

    // Cleanup: revert all changes
    actions.reverse().forEach((action) => {
      if (action.type === 'remove' && element) {
        element.insertBefore(action.element, action.nextSibling)
      } else if (action.type === 'width') {
        action.element.style.width = action.oldWidth || ''
      }
    })
  }, [measuredRef, isFirstRender, segments.map((s) => s.value).join('')])

  return width
}
